import { type ColumnDef, createColumnHelper, type HeaderContext, type Row } from '@tanstack/react-table';
import clsx from 'clsx';
import {
  LuChevronDown,
  LuChevronsUpDown,
  LuCircleDot,
  LuCircleOff,
  LuColumns3,
  LuDroplet,
  LuGrip,
  LuLayers3,
  LuLock,
  LuLockOpen,
  LuMaximize2,
  LuMoveDown,
  LuMoveVertical,
  LuZap,
} from 'react-icons/lu';

import type { PatternDesignOptimisation } from '~api/services';
import { formatDate } from '~utils';

const buildSortingFn = (propGetter: (row: PatternDesignOptimisation) => number) => {
  return (rowA: Row<PatternDesignOptimisation>, rowB: Row<PatternDesignOptimisation>, _: string) => {
    if (rowA.original.constrained !== rowB.original.constrained) {
      return rowA.original.constrained ? -1 : 1;
    }

    return propGetter(rowA.original) > propGetter(rowB.original)
      ? 1
      : propGetter(rowA.original) < propGetter(rowB.original)
        ? -1
        : 0;
  };
};

const objectiveDisplayText = (objective: string) => {
  switch (objective) {
    case 'efficiency':
      return 'Blast Efficiency';
    case 'size':
      return 'Fragmentation Size';
    default:
      return objective;
  }
};

/**
 * Template for column headers with optional icon and sorting indicators.
 *
 * @param icon - Optional icon component to display.
 * @param label - Label text for the header.
 * @param className - Additional CSS classes for styling.
 * @param context - Header context from react-table for sorting information.
 */
const ColumnHeaderTemplate = ({
  icon: Icon,
  label,
  className,
  context,
}: {
  icon?: React.ComponentType<{ size: number }>;
  label?: string;
  className?: string;
  context?: HeaderContext<PatternDesignOptimisation, unknown>;
}) => {
  const canSort = context?.column.getCanSort();
  const sortDirection = context?.column.getIsSorted();

  return (
    <div className={clsx('relative flex grow-0 gap-2', className)}>
      {Icon && <Icon size={14} />}
      <span className="truncate font-bold text-sm">{label}</span>
      {canSort && (
        <>
          <LuChevronsUpDown className={clsx('text-border', { hidden: sortDirection })} size={14} />
          <LuChevronDown
            className={clsx('transition-transform duration-200', {
              hidden: !sortDirection,
              'rotate-180': sortDirection === 'asc',
              'rotate-0': sortDirection === 'desc',
            })}
            size={14}
          />
        </>
      )}
    </div>
  );
};

/**
 * Helper to create columns for PatternDesignOptimisation type.
 */
const columnHelper = createColumnHelper<PatternDesignOptimisation>();

/**
 * Columns definition for the Blast Details table.
 */
const BlastOptimisationsTableColumns = [
  columnHelper.accessor('optimisation_id', {
    minSize: 120,
    maxSize: 150,
    header: () => null,
    cell: (info) => {
      const { optimisation_id, created_at, constrained } = info.row.original;
      return (
        <div className="relative flex flex-col justify-start text-start">
          {!constrained && <div className="absolute top-0 bottom-0 left-0 w-1 bg-destructive" />}
          <div className="flex flex-col gap-0.5 px-5 py-3">
            <div className="flex gap-1">
              {constrained ? (
                <LuLock aria-hidden="true" size={14} />
              ) : (
                <LuLockOpen aria-hidden="true" size={14} color="var(--color-primary)" />
              )}
              <p className="font-bold text-sm leading-tight">#{optimisation_id}</p>
            </div>
            <span className="font-light text-muted-foreground text-xs">{formatDate(new Date(created_at))}</span>
          </div>
        </div>
      );
    },
    enableSorting: false,
  }),
  columnHelper.group({
    id: 'design_parameters',
    header: (context) => (
      <ColumnHeaderTemplate className="items-center justify-center" label="Design Parameters" context={context} />
    ),
    enableSorting: false,
    columns: [
      columnHelper.accessor('efficiency', {
        minSize: 200,
        maxSize: 300,
        size: 250,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuZap}
            label="Efficiency"
            context={context}
          />
        ),
        cell: (info) => (
          <div className="flex flex-col gap-0.5 p-3">
            <div className="flex grow justify-between text-xs">
              <span className="font-light text-muted-foreground">
                {objectiveDisplayText(info.row.original.objective)}
              </span>
              <span className="font-medium">{(info.getValue() * 100).toFixed(1)}%</span>
            </div>
            <div className="mt-1 h-2 grow rounded-full bg-border">
              <div
                className="h-2 rounded-full bg-success transition-all duration-300"
                style={{ width: `${info.getValue() * 100}%` }}
              />
            </div>
          </div>
        ),
        sortingFn: buildSortingFn((row) => row.efficiency),
      }),
      columnHelper.accessor('size_delta', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuCircleDot}
            label="Size Delta"
            context={context}
          />
        ),
        cell: (info) => (info.getValue() != null ? `${info.getValue().toFixed(1)} cm` : 'N/A'),
        sortingFn: buildSortingFn((row) => -(row.size_delta ?? 0)),
      }),
      columnHelper.accessor('bench_height', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuMoveVertical}
            label="Bench Height"
            context={context}
          />
        ),
        cell: (info) => (info.getValue() != null ? `${info.getValue()} m` : 'N/A'),
        sortingFn: buildSortingFn((row) => row.bench_height),
      }),
      columnHelper.accessor('burden', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuMaximize2}
            label="Burden"
            context={context}
          />
        ),
        cell: (info) => `${info.getValue()} m`,
        sortingFn: buildSortingFn((row) => row.burden),
      }),
      columnHelper.accessor('spacing', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuColumns3}
            label="Spacing"
            context={context}
          />
        ),
        cell: (info) => `${info.getValue()} m`,
        sortingFn: buildSortingFn((row) => row.spacing),
      }),
      columnHelper.accessor('diameter', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuCircleOff}
            label="Diameter"
            context={context}
          />
        ),
        cell: (info) => `${(info.getValue() * 1000).toFixed(0)} mm`,
        sortingFn: buildSortingFn((row) => row.diameter),
      }),
      columnHelper.accessor('holes', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate className="items-center justify-center" icon={LuGrip} label="Holes" context={context} />
        ),
        sortingFn: buildSortingFn((row) => row.holes),
      }),
    ],
  }),
  columnHelper.group({
    id: 'statistics',
    header: (context) => (
      <ColumnHeaderTemplate className="items-center justify-center" label="Statistics" context={context} />
    ),
    columns: [
      columnHelper.accessor('statistics.density_mode', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuDroplet}
            label="Explosive Density"
            context={context}
          />
        ),
        cell: (info) => info.getValue(),
        sortingFn: buildSortingFn((row) => row.statistics?.density_mode ?? 0),
      }),
      columnHelper.accessor('statistics.stem_mode', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuLayers3}
            label="Stem Mode"
            context={context}
          />
        ),
        cell: (info) => `${info.getValue()} m`,
        sortingFn: buildSortingFn((row) => row.statistics?.stem_mode),
      }),
      columnHelper.accessor('statistics.total_depth', {
        minSize: 130,
        maxSize: 170,
        size: 150,
        header: (context) => (
          <ColumnHeaderTemplate
            className="items-center justify-center"
            icon={LuMoveDown}
            label="Total Depth"
            context={context}
          />
        ),
        cell: (info) => `${info.getValue()} m`,
        sortingFn: buildSortingFn((row) => row.statistics?.total_depth),
      }),
    ],
  }),
] as ColumnDef<PatternDesignOptimisation, unknown>[];

export { BlastOptimisationsTableColumns };
